import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Plane, AlertTriangle, TrendingUp, BarChart3, Settings, Upload, 
  CheckCircle2, Clock, Eye, Link, Filter, RefreshCw, Download,
  Activity, Target, Gauge, ChevronRight, FileText, Database, Search, PlaneLanding, PlaneTakeoff, AlertCircle, ArrowUpRight, ArrowDownRight, Plug, Webhook, Server, FileUp, Trash2, TestTube, Play, History, Globe, Copy, Brain, Sparkles, Zap, Bell, BellRing, Route, Shield, Pencil
} from 'lucide-react';

// Import FDM Tab Components
import {
  FDMDashboardTab,
  FDMFlightsTab,
  FDMExceedancesTab,
  FDMTrendsTab,
  FDMThresholdsTab,
  FDMIntegrationTab,
  FDMAnalysisTab,
  FDMPredictiveTab
} from '@/components/fdm';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

export default function FDMPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Dashboard data
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // Flights data
  const [flights, setFlights] = useState([]);
  const [flightsTotal, setFlightsTotal] = useState(0);
  
  // Exceedances data
  const [exceedances, setExceedances] = useState([]);
  const [exceedancesTotal, setExceedancesTotal] = useState(0);
  
  // Thresholds data
  const [thresholds, setThresholds] = useState([]);
  
  // Trends data
  const [trendsData, setTrendsData] = useState([]);
  const [trendsByCategory, setTrendsByCategory] = useState([]);
  const [trendsByPhase, setTrendsByPhase] = useState([]);
  
  // Filters
  const [flightFilters, setFlightFilters] = useState({});
  const [exceedanceFilters, setExceedanceFilters] = useState({});
  
  // Dialogs
  const [showFlightDetail, setShowFlightDetail] = useState(null);
  const [showExceedanceDetail, setShowExceedanceDetail] = useState(null);
  const [showThresholdDialog, setShowThresholdDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showProviderDialog, setShowProviderDialog] = useState(false);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [showEditProviderDialog, setShowEditProviderDialog] = useState(false);
  const [showProviderDetailDialog, setShowProviderDetailDialog] = useState(null);
  
  // Categories and phases for filters
  const [categories, setCategories] = useState([]);
  const [phases, setPhases] = useState([]);
  const [aircraftTypes, setAircraftTypes] = useState([]);
  
  // Integration state
  const [providers, setProviders] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [newProvider, setNewProvider] = useState({
    provider_type: 'custom',
    name: '',
    api_url: '',
    api_key: '',
    auto_sync_enabled: false,
    sync_interval_minutes: 60,
    webhook_enabled: true
  });
  const [editingProvider, setEditingProvider] = useState(null);
  
  // Data Generator state
  const [generatorPresets, setGeneratorPresets] = useState({});
  const [generating, setGenerating] = useState(false);
  const [showGeneratorDialog, setShowGeneratorDialog] = useState(false);
  const [generatorConfig, setGeneratorConfig] = useState({
    preset: 'medium',
    custom: false,
    num_flights: 20,
    days_back: 30,
    exceedance_probability: 0.35,
    max_exceedances_per_flight: 3
  });
  const [lastGenerationResult, setLastGenerationResult] = useState(null);
  
  // Analysis rules state
  const [analysisRules, setAnalysisRules] = useState([]);
  const [ruleExecutions, setRuleExecutions] = useState([]);
  const [ruleActions, setRuleActions] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    trigger_type: 'single_exceedance',
    categories: [],
    min_severity: 'level_3',
    actions: ['flag_for_review'],
    auto_incident_severity: 'major',
    priority: 5
  });

  // Predictive Alerts state
  const [predictiveDashboard, setPredictiveDashboard] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [predictiveAlerts, setPredictiveAlerts] = useState([]);
  const [generatingPredictions, setGeneratingPredictions] = useState(false);
  const [showRouteProfileDialog, setShowRouteProfileDialog] = useState(false);
  const [routeProfile, setRouteProfile] = useState(null);
  const [routeProfileForm, setRouteProfileForm] = useState({
    departure_airport: '',
    arrival_airport: '',
    aircraft_type: ''
  });

  const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Fetch dashboard data
  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm/stats`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setDashboardStats(data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  };

  // Fetch flights
  const fetchFlights = async () => {
    try {
      const params = new URLSearchParams();
      if (flightFilters.status) params.append('status', flightFilters.status);
      if (flightFilters.aircraft_type) params.append('aircraft_type', flightFilters.aircraft_type);
      if (flightFilters.has_exceedances !== undefined) params.append('has_exceedances', flightFilters.has_exceedances);
      params.append('limit', '50');
      
      const res = await fetch(`${API_URL}/api/fdm/flights?${params}`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        // Backend returns array directly
        const flightsList = Array.isArray(data) ? data : (data.flights || []);
        setFlights(flightsList);
        setFlightsTotal(flightsList.length);
      }
    } catch (err) {
      console.error('Error fetching flights:', err);
    }
  };

  // Fetch exceedances
  const fetchExceedances = async () => {
    try {
      const params = new URLSearchParams();
      if (exceedanceFilters.category) params.append('category', exceedanceFilters.category);
      if (exceedanceFilters.severity) params.append('severity', exceedanceFilters.severity);
      if (exceedanceFilters.status) params.append('status', exceedanceFilters.status);
      params.append('limit', '50');
      
      const res = await fetch(`${API_URL}/api/fdm/exceedances?${params}`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        // Backend returns array directly
        const excList = Array.isArray(data) ? data : (data.exceedances || []);
        setExceedances(excList);
        setExceedancesTotal(excList.length);
      }
    } catch (err) {
      console.error('Error fetching exceedances:', err);
    }
  };

  // Fetch thresholds
  const fetchThresholds = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm/thresholds`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        // Backend returns array directly
        setThresholds(Array.isArray(data) ? data : (data.thresholds || []));
      }
    } catch (err) {
      console.error('Error fetching thresholds:', err);
    }
  };

  // Fetch trends
  const fetchTrends = async () => {
    try {
      const trendsRes = await fetch(`${API_URL}/api/fdm/trends?period=monthly&months=6`, { headers: apiHeaders });
      
      if (trendsRes.ok) {
        const data = await trendsRes.json();
        setTrendsData(Array.isArray(data) ? data : []);
        
        // Calculate category aggregation from trends
        const catAgg = {};
        const phaseAgg = {};
        (Array.isArray(data) ? data : []).forEach(trend => {
          Object.entries(trend.by_category || {}).forEach(([cat, count]) => {
            catAgg[cat] = (catAgg[cat] || 0) + count;
          });
          Object.entries(trend.by_phase || {}).forEach(([phase, count]) => {
            phaseAgg[phase] = (phaseAgg[phase] || 0) + count;
          });
        });
        
        setTrendsByCategory(Object.entries(catAgg).map(([category, total]) => ({ category, total })).sort((a, b) => b.total - a.total));
        setTrendsByPhase(Object.entries(phaseAgg).map(([phase, count]) => ({ phase, count })).sort((a, b) => b.count - a.count));
      }
    } catch (err) {
      console.error('Error fetching trends:', err);
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const [catRes, phaseRes] = await Promise.all([
        fetch(`${API_URL}/api/fdm/categories`, { headers: apiHeaders }),
        fetch(`${API_URL}/api/fdm/phases`, { headers: apiHeaders })
      ]);
      
      if (catRes.ok) {
        const data = await catRes.json();
        setCategories(data || []);
      }
      if (phaseRes.ok) {
        const data = await phaseRes.json();
        setPhases(data || []);
      }
      
      // Get aircraft types from flights
      const typesRes = await fetch(`${API_URL}/api/fdm/flights?limit=100`, { headers: apiHeaders });
      if (typesRes.ok) {
        const data = await typesRes.json();
        const types = [...new Set((data.flights || []).map(f => f.flight_info?.aircraft_type).filter(Boolean))];
        setAircraftTypes(types);
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  // Seed demo data
  const seedDemoData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/fdm/seed`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Demo data seeded: ${data.flights_created} flights, ${data.exceedances_created} exceedances`);
        await Promise.all([fetchDashboard(), fetchFlights(), fetchExceedances(), fetchThresholds()]);
      } else {
        toast.error('Failed to seed demo data');
      }
    } catch (err) {
      toast.error('Error seeding demo data');
    } finally {
      setLoading(false);
    }
  };

  // Review exceedance
  const reviewExceedance = async (exceedanceId, notes) => {
    try {
      const res = await fetch(`${API_URL}/api/fdm/exceedances/${exceedanceId}`, {
        method: 'PUT',
        headers: apiHeaders,
        body: JSON.stringify({
          reviewed: true,
          review_notes: notes,
          status: 'under_review'
        })
      });
      if (res.ok) {
        toast.success('Exceedance marked as reviewed');
        fetchExceedances();
        fetchDashboard();
      }
    } catch (err) {
      toast.error('Error updating exceedance');
    }
  };

  // === INTEGRATION FUNCTIONS ===
  
  // Fetch providers
  const fetchProviders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-integration/providers`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setProviders(data.providers || []);
      }
    } catch (err) {
      console.error('Error fetching providers:', err);
    }
  };

  // Fetch provider types
  const fetchProviderTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-integration/providers/types`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setProviderTypes(data.providers || []);
      }
    } catch (err) {
      console.error('Error fetching provider types:', err);
    }
  };

  // Fetch upload history
  const fetchUploadHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-integration/upload/history?limit=20`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setUploadHistory(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching upload history:', err);
    }
  };

  // Fetch webhook logs
  const fetchWebhookLogs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-integration/webhook/logs?limit=20`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setWebhookLogs(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching webhook logs:', err);
    }
  };

  // Upload file
  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    setUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      setUploadProgress(30);

      const res = await fetch(`${API_URL}/api/fdm-integration/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      setUploadProgress(80);

      if (res.ok) {
        const data = await res.json();
        toast.success(`Upload complete: ${data.flights_processed || 0} flights, ${data.exceedances_detected || 0} exceedances`);
        setShowUploadDialog(false);
        setSelectedFile(null);
        await Promise.all([fetchDashboard(), fetchFlights(), fetchExceedances(), fetchUploadHistory()]);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch (err) {
      toast.error('Error uploading file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Create provider
  const createProvider = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-integration/providers`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify(newProvider)
      });

      if (res.ok) {
        toast.success('Provider created successfully');
        setShowProviderDialog(false);
        setNewProvider({
          provider_type: 'custom',
          name: '',
          api_url: '',
          api_key: '',
          auto_sync_enabled: false,
          sync_interval_minutes: 60,
          webhook_enabled: true
        });
        fetchProviders();
      } else {
        toast.error('Failed to create provider');
      }
    } catch (err) {
      toast.error('Error creating provider');
    }
  };

  // Test provider connection
  const testProviderConnection = async (providerId) => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-integration/providers/${providerId}/test`, {
        method: 'POST',
        headers: apiHeaders
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          toast.success('Connection successful');
        } else {
          toast.error(data.error || 'Connection failed');
        }
        fetchProviders();
      }
    } catch (err) {
      toast.error('Error testing connection');
    }
  };

  // Sync from provider
  const syncFromProvider = async (providerId) => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-integration/providers/${providerId}/sync`, {
        method: 'POST',
        headers: apiHeaders
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Sync initiated');
        await Promise.all([fetchDashboard(), fetchFlights(), fetchProviders()]);
      }
    } catch (err) {
      toast.error('Error syncing from provider');
    }
  };

  // Delete provider
  const deleteProvider = async (providerId) => {
    if (!confirm('Are you sure you want to delete this provider?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/fdm-integration/providers/${providerId}`, {
        method: 'DELETE',
        headers: apiHeaders
      });
      
      if (res.ok) {
        toast.success('Provider deleted');
        fetchProviders();
      }
    } catch (err) {
      toast.error('Error deleting provider');
    }
  };

  // Open edit provider dialog
  const openEditProvider = (provider) => {
    setEditingProvider({
      ...provider,
      api_key: '' // Don't show existing key
    });
    setShowEditProviderDialog(true);
  };

  // Update provider
  const updateProvider = async () => {
    if (!editingProvider?.id) return;
    
    try {
      const updates = {
        name: editingProvider.name,
        api_url: editingProvider.api_url,
        webhook_enabled: editingProvider.webhook_enabled,
        auto_sync_enabled: editingProvider.auto_sync_enabled,
        sync_interval_minutes: editingProvider.sync_interval_minutes,
        is_active: editingProvider.is_active
      };
      
      // Only include api_key if it was changed
      if (editingProvider.api_key) {
        updates.api_key = editingProvider.api_key;
      }
      
      const res = await fetch(`${API_URL}/api/fdm-integration/providers/${editingProvider.id}`, {
        method: 'PUT',
        headers: apiHeaders,
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        toast.success('Provider updated');
        setShowEditProviderDialog(false);
        setEditingProvider(null);
        fetchProviders();
      } else {
        toast.error('Failed to update provider');
      }
    } catch (err) {
      toast.error('Error updating provider');
    }
  };

  // === DATA GENERATOR FUNCTIONS ===

  // Fetch generator presets
  const fetchGeneratorPresets = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-generator/presets`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setGeneratorPresets(data.presets || {});
      }
    } catch (err) {
      console.error('Error fetching generator presets:', err);
    }
  };

  // Generate sample data with preset
  const generateSampleData = async (preset) => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/fdm-generator/generate/quick`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify({ preset })
      });
      
      if (res.ok) {
        const data = await res.json();
        setLastGenerationResult(data);
        toast.success(`Generated ${data.flights_created} flights with ${data.exceedances_created} exceedances`);
        // Refresh data
        fetchDashboard();
        fetchFlights();
        fetchExceedances();
      } else {
        toast.error('Failed to generate sample data');
      }
    } catch (err) {
      toast.error('Error generating sample data');
    } finally {
      setGenerating(false);
    }
  };

  // Generate with custom config
  const generateCustomData = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/fdm-generator/generate/custom`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify({
          num_flights: generatorConfig.num_flights,
          days_back: generatorConfig.days_back,
          exceedance_probability: generatorConfig.exceedance_probability,
          max_exceedances_per_flight: generatorConfig.max_exceedances_per_flight
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setLastGenerationResult(data);
        toast.success(`Generated ${data.flights_created} flights with ${data.exceedances_created} exceedances`);
        setShowGeneratorDialog(false);
        // Refresh data
        fetchDashboard();
        fetchFlights();
        fetchExceedances();
      } else {
        toast.error('Failed to generate sample data');
      }
    } catch (err) {
      toast.error('Error generating sample data');
    } finally {
      setGenerating(false);
    }
  };

  // Clear generated data
  const clearGeneratedData = async () => {
    if (!confirm('Are you sure you want to clear all generated/simulated flight data? This cannot be undone.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/fdm-generator/clear`, {
        method: 'DELETE',
        headers: apiHeaders
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Cleared ${data.flights_deleted} flights and ${data.exceedances_deleted} exceedances`);
        setLastGenerationResult(null);
        // Refresh data
        fetchDashboard();
        fetchFlights();
        fetchExceedances();
      } else {
        toast.error('Failed to clear data');
      }
    } catch (err) {
      toast.error('Error clearing data');
    }
  };

  // === ANALYSIS RULES FUNCTIONS ===
  
  // Fetch analysis rules
  const fetchAnalysisRules = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-analysis/rules`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setAnalysisRules(data.rules || []);
      }
    } catch (err) {
      console.error('Error fetching analysis rules:', err);
    }
  };

  // Fetch rule actions
  const fetchRuleActions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-analysis/actions`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setRuleActions(data.actions || []);
      }
    } catch (err) {
      console.error('Error fetching rule actions:', err);
    }
  };

  // Fetch rule executions
  const fetchRuleExecutions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-analysis/history?limit=20`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setRuleExecutions(data.executions || []);
      }
    } catch (err) {
      console.error('Error fetching rule executions:', err);
    }
  };

  // Seed default rules
  const seedDefaultRules = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-analysis/rules/seed`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Seeded ${data.created} default analysis rules`);
        fetchAnalysisRules();
      }
    } catch (err) {
      toast.error('Error seeding rules');
    }
  };

  // Create analysis rule
  const createRule = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-analysis/rules`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify(newRule)
      });
      if (res.ok) {
        toast.success('Rule created successfully');
        setShowRuleDialog(false);
        setNewRule({
          name: '',
          description: '',
          trigger_type: 'single_exceedance',
          categories: [],
          min_severity: 'level_3',
          actions: ['flag_for_review'],
          auto_incident_severity: 'major',
          priority: 5
        });
        fetchAnalysisRules();
      } else {
        toast.error('Failed to create rule');
      }
    } catch (err) {
      toast.error('Error creating rule');
    }
  };

  // Toggle rule status
  const toggleRuleStatus = async (ruleId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      const res = await fetch(`${API_URL}/api/fdm-analysis/rules/${ruleId}`, {
        method: 'PUT',
        headers: apiHeaders,
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success(`Rule ${newStatus}`);
        fetchAnalysisRules();
      }
    } catch (err) {
      toast.error('Error updating rule');
    }
  };

  // Run batch analysis
  const runBatchAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API_URL}/api/fdm-analysis/analyze-batch?hours_back=24`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Analyzed ${data.exceedances_analyzed} exceedances, ${data.rules_triggered} rules triggered, ${data.incidents_created} incidents created`);
        fetchRuleExecutions();
        fetchExceedances();
        fetchDashboard();
      }
    } catch (err) {
      toast.error('Error running analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  // === PREDICTIVE ALERTS FUNCTIONS ===

  // Fetch predictive dashboard
  const fetchPredictiveDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-predictive/dashboard`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setPredictiveDashboard(data);
      }
    } catch (err) {
      console.error('Error fetching predictive dashboard:', err);
    }
  };

  // Fetch predictions
  const fetchPredictions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-predictive/predictions?limit=50`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setPredictions(data.predictions || []);
      }
    } catch (err) {
      console.error('Error fetching predictions:', err);
    }
  };

  // Fetch predictive alerts
  const fetchPredictiveAlerts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-predictive/alerts`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setPredictiveAlerts(data.alerts || []);
      }
    } catch (err) {
      console.error('Error fetching predictive alerts:', err);
    }
  };

  // Generate AI predictions
  const generatePredictions = async () => {
    setGeneratingPredictions(true);
    try {
      const res = await fetch(`${API_URL}/api/fdm-predictive/generate`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Generated ${data.predictions_generated} predictions, ${data.alerts_created} high-risk alerts created`);
        fetchPredictiveDashboard();
        fetchPredictions();
        fetchPredictiveAlerts();
      } else {
        toast.error('Failed to generate predictions');
      }
    } catch (err) {
      toast.error('Error generating predictions');
    } finally {
      setGeneratingPredictions(false);
    }
  };

  // Generate route risk profile
  const generateRouteProfile = async () => {
    if (!routeProfileForm.departure_airport || !routeProfileForm.arrival_airport) {
      toast.error('Please enter departure and arrival airports');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/fdm-predictive/route-profile`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify(routeProfileForm)
      });
      if (res.ok) {
        const data = await res.json();
        setRouteProfile(data);
        toast.success('Route risk profile generated');
      } else {
        toast.error('Failed to generate route profile');
      }
    } catch (err) {
      toast.error('Error generating route profile');
    }
  };

  // Acknowledge alert
  const acknowledgeAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-predictive/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        toast.success('Alert acknowledged');
        fetchPredictiveAlerts();
        fetchPredictiveDashboard();
      }
    } catch (err) {
      toast.error('Error acknowledging alert');
    }
  };

  // Dismiss alert
  const dismissAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/fdm-predictive/alerts/${alertId}/dismiss`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        toast.success('Alert dismissed');
        fetchPredictiveAlerts();
        fetchPredictiveDashboard();
      }
    } catch (err) {
      toast.error('Error dismissing alert');
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchFlights();
    fetchExceedances();
    fetchThresholds();
    fetchTrends();
    fetchFilterOptions();
    fetchProviders();
    fetchProviderTypes();
    fetchUploadHistory();
    fetchWebhookLogs();
    fetchAnalysisRules();
    fetchRuleActions();
    fetchRuleExecutions();
    fetchPredictiveDashboard();
    fetchPredictions();
    fetchPredictiveAlerts();
    fetchGeneratorPresets();
  }, []);

  useEffect(() => {
    fetchFlights();
  }, [flightFilters]);

  useEffect(() => {
    fetchExceedances();
  }, [exceedanceFilters]);

  const getSeverityColor = (severity) => {
    const colors = {
      level_1: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      level_2: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      level_3: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      level_4: 'bg-red-500/20 text-red-400 border-red-500/30',
      level_5: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    };
    return colors[severity] || 'bg-slate-500/20 text-slate-400';
  };

  const getSeverityLabel = (severity) => {
    const labels = {
      level_1: 'Level 1 - Minor',
      level_2: 'Level 2 - Moderate',
      level_3: 'Level 3 - Serious',
      level_4: 'Level 4 - Severe',
      level_5: 'Level 5 - Critical',
    };
    return labels[severity] || severity;
  };

  const formatCategory = (category) => {
    return category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      low: 'bg-green-500/20 text-green-400 border-green-500/30',
      moderate: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return colors[level] || 'bg-slate-500/20 text-slate-400';
  };

  const getConfidenceColor = (confidence) => {
    const colors = {
      very_high: 'text-green-400',
      high: 'text-blue-400',
      medium: 'text-yellow-400',
      low: 'text-slate-400',
    };
    return colors[confidence] || 'text-slate-400';
  };

  return (
    <div className="p-6 space-y-6" data-testid="fdm-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">{t('fdm.title')}</h1>
          <p className="text-slate-400 mt-1">{t('fdm.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowUploadDialog(true)} data-testid="upload-data-btn">
            <Upload className="w-4 h-4 mr-2" />
            {t('common.upload')}
          </Button>
          <Button variant="outline" size="sm" onClick={seedDemoData} disabled={loading} data-testid="seed-demo-btn">
            <Database className="w-4 h-4 mr-2" />
            {loading ? t('common.loading') : t('common.seedData')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="w-4 h-4 mr-2" />
            {t('common.dashboard')}
          </TabsTrigger>
          <TabsTrigger value="flights" data-testid="tab-flights">
            <Plane className="w-4 h-4 mr-2" />
            {t('fdm.flights')}
          </TabsTrigger>
          <TabsTrigger value="exceedances" data-testid="tab-exceedances">
            <AlertTriangle className="w-4 h-4 mr-2" />
            {t('fdm.exceedances')}
          </TabsTrigger>
          <TabsTrigger value="trends" data-testid="tab-trends">
            <TrendingUp className="w-4 h-4 mr-2" />
            {t('fdm.trends')}
          </TabsTrigger>
          <TabsTrigger value="thresholds" data-testid="tab-thresholds">
            <Settings className="w-4 h-4 mr-2" />
            {t('fdm.thresholds')}
          </TabsTrigger>
          <TabsTrigger value="integration" data-testid="tab-integration">
            <Plug className="w-4 h-4 mr-2" />
            {t('fdm.integration')}
          </TabsTrigger>
          <TabsTrigger value="analysis" data-testid="tab-analysis">
            <Target className="w-4 h-4 mr-2" />
            {t('fdm.autoAnalysis')}
          </TabsTrigger>
          <TabsTrigger value="predictive" data-testid="tab-predictive">
            <Brain className="w-4 h-4 mr-2" />
            {t('fdm.aiPredictions')}
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6 mt-6">
          <FDMDashboardTab
            dashboardStats={dashboardStats}
            exceedances={exceedances}
            onNavigateToExceedances={() => setActiveTab('exceedances')}
          />
        </TabsContent>

        {/* Flights Tab */}
        <TabsContent value="flights" className="space-y-4 mt-6">
          <FDMFlightsTab
            flights={flights}
            flightsTotal={flightsTotal}
            flightFilters={flightFilters}
            setFlightFilters={setFlightFilters}
            aircraftTypes={aircraftTypes}
            onFlightClick={setShowFlightDetail}
          />
        </TabsContent>

        {/* Exceedances Tab */}
        <TabsContent value="exceedances" className="space-y-4 mt-6">
          <FDMExceedancesTab
            exceedances={exceedances}
            exceedancesTotal={exceedancesTotal}
            exceedanceFilters={exceedanceFilters}
            setExceedanceFilters={setExceedanceFilters}
            categories={categories}
            onExceedanceClick={setShowExceedanceDetail}
          />
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-6 mt-6">
          <FDMTrendsTab
            trendsData={trendsData}
            trendsByCategory={trendsByCategory}
            trendsByPhase={trendsByPhase}
          />
        </TabsContent>

        {/* Thresholds Tab */}
        <TabsContent value="thresholds" className="space-y-4 mt-6">
          <FDMThresholdsTab
            thresholds={thresholds}
            onAddThreshold={() => setShowThresholdDialog(true)}
          />
        </TabsContent>

        {/* Integration Tab */}
        <TabsContent value="integration" className="space-y-6 mt-6">
          <FDMIntegrationTab
            providers={providers}
            providerTypes={providerTypes}
            uploadHistory={uploadHistory}
            webhookLogs={webhookLogs}
            generatorPresets={generatorPresets}
            generating={generating}
            lastGenerationResult={lastGenerationResult}
            onShowUploadDialog={() => setShowUploadDialog(true)}
            onShowProviderDialog={() => setShowProviderDialog(true)}
            onShowGeneratorDialog={() => setShowGeneratorDialog(true)}
            onTestProviderConnection={testProviderConnection}
            onSyncFromProvider={syncFromProvider}
            onOpenEditProvider={openEditProvider}
            onDeleteProvider={deleteProvider}
            onGenerateSampleData={generateSampleData}
            onClearGeneratedData={clearGeneratedData}
          />
        </TabsContent>

        {/* Analysis Tab */}
        <TabsContent value="analysis" className="space-y-6 mt-6">
          <FDMAnalysisTab
            analysisRules={analysisRules}
            ruleExecutions={ruleExecutions}
            analyzing={analyzing}
            onSeedDefaultRules={seedDefaultRules}
            onRunBatchAnalysis={runBatchAnalysis}
            onShowRuleDialog={() => setShowRuleDialog(true)}
            onToggleRuleStatus={toggleRuleStatus}
          />
        </TabsContent>

        {/* Predictive Tab */}
        <TabsContent value="predictive" className="space-y-6 mt-6">
          <FDMPredictiveTab
            predictiveDashboard={predictiveDashboard}
            predictions={predictions}
            predictiveAlerts={predictiveAlerts}
            routeProfile={routeProfile}
            generatingPredictions={generatingPredictions}
            onGeneratePredictions={generatePredictions}
            onShowRouteProfileDialog={() => setShowRouteProfileDialog(true)}
            onRefresh={() => { fetchPredictiveDashboard(); fetchPredictions(); fetchPredictiveAlerts(); }}
            onAcknowledgeAlert={acknowledgeAlert}
            onDismissAlert={dismissAlert}
          />
        </TabsContent>
      </Tabs>

      {/* Flight Detail Dialog */}
      <Dialog open={!!showFlightDetail} onOpenChange={() => setShowFlightDetail(null)}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Flight Details</DialogTitle>
          </DialogHeader>
          {showFlightDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-xs text-slate-500">Flight Number</p>
                  <p className="text-lg font-semibold text-slate-200">{showFlightDetail.flight_info?.flight_number}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-xs text-slate-500">Aircraft</p>
                  <p className="text-lg font-semibold text-slate-200">{showFlightDetail.flight_info?.aircraft_registration}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-xs text-slate-500">Route</p>
                  <p className="text-lg font-semibold text-slate-200">
                    {showFlightDetail.flight_info?.departure_airport} → {showFlightDetail.flight_info?.arrival_airport}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-xs text-slate-500">Aircraft Type</p>
                  <p className="text-lg font-semibold text-slate-200">{showFlightDetail.flight_info?.aircraft_type}</p>
                </div>
              </div>
              
              <Separator className="bg-slate-700" />
              
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-2">Exceedances ({showFlightDetail.exceedances_count || 0})</h4>
                {showFlightDetail.exceedances?.length > 0 ? (
                  <div className="space-y-2">
                    {showFlightDetail.exceedances.map((exc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                        <span className="text-sm text-slate-300">{formatCategory(exc.category)}</span>
                        <Badge className={getSeverityColor(exc.severity)}>{exc.severity?.replace('_', ' ')}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No exceedances detected - clean flight</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Exceedance Detail Dialog */}
      <Dialog open={!!showExceedanceDetail} onOpenChange={() => setShowExceedanceDetail(null)}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Exceedance Details</DialogTitle>
          </DialogHeader>
          {showExceedanceDetail && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-200">{formatCategory(showExceedanceDetail.category)}</h3>
                <Badge className={getSeverityColor(showExceedanceDetail.severity)}>
                  {getSeverityLabel(showExceedanceDetail.severity)}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-xs text-slate-500">Flight</p>
                  <p className="font-medium text-slate-200">{showExceedanceDetail.flight_info?.flight_number}</p>
                  <p className="text-sm text-slate-400">{showExceedanceDetail.flight_info?.aircraft_registration}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-xs text-slate-500">Phase</p>
                  <p className="font-medium text-slate-200 capitalize">{showExceedanceDetail.phase?.replace('_', ' ')}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-xs text-slate-500">Recorded Value</p>
                  <p className="font-medium text-slate-200">{showExceedanceDetail.parameter_value} {showExceedanceDetail.unit}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-xs text-slate-500">Threshold</p>
                  <p className="font-medium text-slate-200">{showExceedanceDetail.threshold_value} {showExceedanceDetail.unit}</p>
                </div>
              </div>
              
              <Separator className="bg-slate-700" />
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {showExceedanceDetail.reviewed ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      <span className="text-green-400">Reviewed</span>
                    </>
                  ) : (
                    <>
                      <Clock className="w-5 h-5 text-yellow-400" />
                      <span className="text-yellow-400">Pending Review</span>
                    </>
                  )}
                </div>
                {!showExceedanceDetail.reviewed && (
                  <Button size="sm" onClick={() => {
                    reviewExceedance(showExceedanceDetail.id, 'Reviewed');
                    setShowExceedanceDetail(null);
                  }}>
                    Mark as Reviewed
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Upload Flight Data</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div 
              className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center cursor-pointer hover:border-slate-600 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".csv,.xlsx,.xls,.xml,.json,.qar,.dat"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
              {selectedFile ? (
                <>
                  <FileUp className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-200 font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Click to select file</p>
                  <p className="text-xs text-slate-500 mt-1">CSV, Excel, XML, JSON, or QAR</p>
                </>
              )}
            </div>
            
            {uploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Uploading...</span>
                  <span className="text-slate-300">{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
              </div>
            )}
            
            <div className="text-xs text-slate-500">
              <p className="font-medium text-slate-400">Supported formats:</p>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li><span className="text-green-400">CSV/Excel</span> - Spreadsheet with flight data</li>
                <li><span className="text-blue-400">ACMS XML</span> - Airbus ACMS format</li>
                <li><span className="text-purple-400">QAR Binary</span> - Quick Access Recorder</li>
                <li><span className="text-orange-400">JSON</span> - API integration format</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUploadDialog(false); setSelectedFile(null); }}>Cancel</Button>
            <Button onClick={handleFileUpload} disabled={!selectedFile || uploading}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Provider Dialog */}
      <Dialog open={showProviderDialog} onOpenChange={setShowProviderDialog}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Add FDM Provider</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Provider Type</Label>
              <Select value={newProvider.provider_type} onValueChange={(v) => setNewProvider({...newProvider, provider_type: v})}>
                <SelectTrigger className="bg-slate-800/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flyht">FLYHT (AFIRS)</SelectItem>
                  <SelectItem value="safran">Safran (CASSIOPEE)</SelectItem>
                  <SelectItem value="teledyne">Teledyne (AIRFASE)</SelectItem>
                  <SelectItem value="ge_cfoqa">GE Aviation (C-FOQA)</SelectItem>
                  <SelectItem value="custom">Custom / In-house</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Provider Name</Label>
              <Input 
                value={newProvider.name}
                onChange={(e) => setNewProvider({...newProvider, name: e.target.value})}
                placeholder="e.g., Primary FDM System"
                className="bg-slate-800/50"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">API URL (Optional)</Label>
              <Input 
                value={newProvider.api_url}
                onChange={(e) => setNewProvider({...newProvider, api_url: e.target.value})}
                placeholder="https://api.provider.com/fdm"
                className="bg-slate-800/50"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">API Key (Optional)</Label>
              <Input 
                type="password"
                value={newProvider.api_key}
                onChange={(e) => setNewProvider({...newProvider, api_key: e.target.value})}
                placeholder="Enter API key"
                className="bg-slate-800/50"
              />
            </div>

            <Separator className="bg-slate-700" />

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-slate-300">Enable Webhook</Label>
                <p className="text-xs text-slate-500">Receive data pushed from provider</p>
              </div>
              <Switch 
                checked={newProvider.webhook_enabled}
                onCheckedChange={(v) => setNewProvider({...newProvider, webhook_enabled: v})}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-slate-300">Auto-Sync</Label>
                <p className="text-xs text-slate-500">Periodically pull data from provider</p>
              </div>
              <Switch 
                checked={newProvider.auto_sync_enabled}
                onCheckedChange={(v) => setNewProvider({...newProvider, auto_sync_enabled: v})}
              />
            </div>

            {newProvider.auto_sync_enabled && (
              <div className="space-y-2">
                <Label className="text-slate-300">Sync Interval (minutes)</Label>
                <Select value={String(newProvider.sync_interval_minutes)} onValueChange={(v) => setNewProvider({...newProvider, sync_interval_minutes: parseInt(v)})}>
                  <SelectTrigger className="bg-slate-800/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">Every 15 minutes</SelectItem>
                    <SelectItem value="30">Every 30 minutes</SelectItem>
                    <SelectItem value="60">Every hour</SelectItem>
                    <SelectItem value="180">Every 3 hours</SelectItem>
                    <SelectItem value="360">Every 6 hours</SelectItem>
                    <SelectItem value="720">Every 12 hours</SelectItem>
                    <SelectItem value="1440">Daily</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProviderDialog(false)}>Cancel</Button>
            <Button onClick={createProvider} disabled={!newProvider.name}>Create Provider</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Provider Dialog */}
      <Dialog open={showEditProviderDialog} onOpenChange={setShowEditProviderDialog}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Edit FDM Provider</DialogTitle>
          </DialogHeader>
          {editingProvider && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Provider Type</Label>
                <Input 
                  value={editingProvider.provider_type?.toUpperCase()}
                  disabled
                  className="bg-slate-800/50 text-slate-400"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Provider Name</Label>
                <Input 
                  value={editingProvider.name}
                  onChange={(e) => setEditingProvider({...editingProvider, name: e.target.value})}
                  placeholder="e.g., Primary FDM System"
                  className="bg-slate-800/50"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">API URL</Label>
                <Input 
                  value={editingProvider.api_url || ''}
                  onChange={(e) => setEditingProvider({...editingProvider, api_url: e.target.value})}
                  placeholder="https://api.provider.com/fdm"
                  className="bg-slate-800/50"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">API Key (leave blank to keep existing)</Label>
                <Input 
                  type="password"
                  value={editingProvider.api_key || ''}
                  onChange={(e) => setEditingProvider({...editingProvider, api_key: e.target.value})}
                  placeholder="Enter new API key"
                  className="bg-slate-800/50"
                />
              </div>

              <Separator className="bg-slate-700" />

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-slate-300">Active</Label>
                  <p className="text-xs text-slate-500">Enable/disable this provider</p>
                </div>
                <Switch 
                  checked={editingProvider.is_active}
                  onCheckedChange={(v) => setEditingProvider({...editingProvider, is_active: v})}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-slate-300">Enable Webhook</Label>
                  <p className="text-xs text-slate-500">Receive data pushed from provider</p>
                </div>
                <Switch 
                  checked={editingProvider.webhook_enabled}
                  onCheckedChange={(v) => setEditingProvider({...editingProvider, webhook_enabled: v})}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-slate-300">Auto-Sync</Label>
                  <p className="text-xs text-slate-500">Periodically pull data from provider</p>
                </div>
                <Switch 
                  checked={editingProvider.auto_sync_enabled}
                  onCheckedChange={(v) => setEditingProvider({...editingProvider, auto_sync_enabled: v})}
                />
              </div>

              {editingProvider.auto_sync_enabled && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Sync Interval (minutes)</Label>
                  <Select value={String(editingProvider.sync_interval_minutes)} onValueChange={(v) => setEditingProvider({...editingProvider, sync_interval_minutes: parseInt(v)})}>
                    <SelectTrigger className="bg-slate-800/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">Every 15 minutes</SelectItem>
                      <SelectItem value="30">Every 30 minutes</SelectItem>
                      <SelectItem value="60">Every hour</SelectItem>
                      <SelectItem value="180">Every 3 hours</SelectItem>
                      <SelectItem value="360">Every 6 hours</SelectItem>
                      <SelectItem value="720">Every 12 hours</SelectItem>
                      <SelectItem value="1440">Daily</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {editingProvider.webhook_secret && (
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <p className="text-xs text-slate-400 mb-1">Webhook Secret</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm text-slate-300 flex-1">{editingProvider.webhook_secret}</code>
                    <Button variant="ghost" size="sm" onClick={() => {
                      navigator.clipboard.writeText(editingProvider.webhook_secret);
                      toast.success('Webhook secret copied');
                    }}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {editingProvider.last_sync_at && (
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <p className="text-xs text-slate-400">Last Sync: {new Date(editingProvider.last_sync_at).toLocaleString()}</p>
                  {editingProvider.next_sync_at && (
                    <p className="text-xs text-slate-400">Next Sync: {new Date(editingProvider.next_sync_at).toLocaleString()}</p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditProviderDialog(false); setEditingProvider(null); }}>Cancel</Button>
            <Button onClick={updateProvider} disabled={!editingProvider?.name}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Analysis Rule Dialog */}
      <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Add Analysis Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Rule Name</Label>
              <Input 
                value={newRule.name}
                onChange={(e) => setNewRule({...newRule, name: e.target.value})}
                placeholder="e.g., Critical Exceedance Alert"
                className="bg-slate-800/50"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Description</Label>
              <Textarea 
                value={newRule.description}
                onChange={(e) => setNewRule({...newRule, description: e.target.value})}
                placeholder="What this rule does..."
                className="bg-slate-800/50"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Trigger Type</Label>
                <Select value={newRule.trigger_type} onValueChange={(v) => setNewRule({...newRule, trigger_type: v})}>
                  <SelectTrigger className="bg-slate-800/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single_exceedance">Single Exceedance</SelectItem>
                    <SelectItem value="multiple_exceedances">Multiple Exceedances</SelectItem>
                    <SelectItem value="recurring_exceedance">Recurring (Aircraft)</SelectItem>
                    <SelectItem value="fleet_trend">Fleet Trend</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Min Severity</Label>
                <Select value={newRule.min_severity} onValueChange={(v) => setNewRule({...newRule, min_severity: v})}>
                  <SelectTrigger className="bg-slate-800/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="level_1">Level 1 - Minor</SelectItem>
                    <SelectItem value="level_2">Level 2 - Moderate</SelectItem>
                    <SelectItem value="level_3">Level 3 - Serious</SelectItem>
                    <SelectItem value="level_4">Level 4 - Severe</SelectItem>
                    <SelectItem value="level_5">Level 5 - Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Actions (Select Multiple)</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'flag_for_review', name: 'Flag for Review' },
                  { id: 'flag_for_investigation', name: 'Flag for Investigation' },
                  { id: 'create_incident', name: 'Create Incident' },
                  { id: 'notify_safety_team', name: 'Notify Safety Team' },
                  { id: 'notify_management', name: 'Notify Management' },
                  { id: 'escalate', name: 'Escalate' }
                ].map(action => (
                  <div 
                    key={action.id}
                    className={`p-2 rounded-lg border cursor-pointer transition-colors ${
                      newRule.actions.includes(action.id) 
                        ? 'bg-purple-500/20 border-purple-500/50 text-purple-300' 
                        : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600'
                    }`}
                    onClick={() => {
                      const actions = newRule.actions.includes(action.id)
                        ? newRule.actions.filter(a => a !== action.id)
                        : [...newRule.actions, action.id];
                      setNewRule({...newRule, actions});
                    }}
                  >
                    <span className="text-sm">{action.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {newRule.actions.includes('create_incident') && (
              <div className="space-y-2">
                <Label className="text-slate-300">Auto-Incident Severity</Label>
                <Select value={newRule.auto_incident_severity} onValueChange={(v) => setNewRule({...newRule, auto_incident_severity: v})}>
                  <SelectTrigger className="bg-slate-800/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="negligible">Negligible</SelectItem>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="hazardous">Hazardous</SelectItem>
                    <SelectItem value="catastrophic">Catastrophic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-slate-300">Priority (1-10, higher = evaluated first)</Label>
              <Input 
                type="number"
                min={1}
                max={10}
                value={newRule.priority}
                onChange={(e) => setNewRule({...newRule, priority: parseInt(e.target.value) || 5})}
                className="bg-slate-800/50"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRuleDialog(false)}>Cancel</Button>
            <Button onClick={createRule} disabled={!newRule.name || newRule.actions.length === 0}>Create Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Route Risk Profile Dialog */}
      <Dialog open={showRouteProfileDialog} onOpenChange={setShowRouteProfileDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <Route className="w-5 h-5 text-blue-400" />
              Generate Route Risk Profile
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Departure Airport (ICAO)</Label>
              <Input 
                value={routeProfileForm.departure_airport}
                onChange={(e) => setRouteProfileForm({...routeProfileForm, departure_airport: e.target.value.toUpperCase()})}
                placeholder="e.g., OERK"
                maxLength={4}
                className="bg-slate-800/50"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Arrival Airport (ICAO)</Label>
              <Input 
                value={routeProfileForm.arrival_airport}
                onChange={(e) => setRouteProfileForm({...routeProfileForm, arrival_airport: e.target.value.toUpperCase()})}
                placeholder="e.g., OEJN"
                maxLength={4}
                className="bg-slate-800/50"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Aircraft Type (Optional)</Label>
              <Select value={routeProfileForm.aircraft_type || "all"} onValueChange={(v) => setRouteProfileForm({...routeProfileForm, aircraft_type: v === "all" ? "" : v})}>
                <SelectTrigger className="bg-slate-800/50">
                  <SelectValue placeholder="All Aircraft Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="A320">Airbus A320</SelectItem>
                  <SelectItem value="A321">Airbus A321</SelectItem>
                  <SelectItem value="B737">Boeing 737</SelectItem>
                  <SelectItem value="B777">Boeing 777</SelectItem>
                  <SelectItem value="B787">Boeing 787</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRouteProfileDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => { generateRouteProfile(); setShowRouteProfileDialog(false); }}
              disabled={!routeProfileForm.departure_airport || !routeProfileForm.arrival_airport}
            >
              <Brain className="w-4 h-4 mr-2" />
              Generate Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Data Generator Dialog */}
      <Dialog open={showGeneratorDialog} onOpenChange={setShowGeneratorDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-400" />
              Custom Data Generation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Number of Flights</Label>
              <Input 
                type="number"
                min={1}
                max={100}
                value={generatorConfig.num_flights}
                onChange={(e) => setGeneratorConfig({...generatorConfig, num_flights: parseInt(e.target.value) || 10})}
                className="bg-slate-800/50"
              />
              <p className="text-xs text-slate-500">1-100 flights</p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Days Range</Label>
              <Input 
                type="number"
                min={1}
                max={365}
                value={generatorConfig.days_back}
                onChange={(e) => setGeneratorConfig({...generatorConfig, days_back: parseInt(e.target.value) || 30})}
                className="bg-slate-800/50"
              />
              <p className="text-xs text-slate-500">Generate flights within this many days (1-365)</p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Exceedance Probability: {Math.round(generatorConfig.exceedance_probability * 100)}%</Label>
              <input 
                type="range"
                min={0}
                max={100}
                value={generatorConfig.exceedance_probability * 100}
                onChange={(e) => setGeneratorConfig({...generatorConfig, exceedance_probability: parseInt(e.target.value) / 100})}
                className="w-full"
              />
              <p className="text-xs text-slate-500">Probability that a flight will have exceedances</p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Max Exceedances per Flight</Label>
              <Select value={String(generatorConfig.max_exceedances_per_flight)} onValueChange={(v) => setGeneratorConfig({...generatorConfig, max_exceedances_per_flight: parseInt(v)})}>
                <SelectTrigger className="bg-slate-800/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 exceedance</SelectItem>
                  <SelectItem value="2">2 exceedances</SelectItem>
                  <SelectItem value="3">3 exceedances</SelectItem>
                  <SelectItem value="4">4 exceedances</SelectItem>
                  <SelectItem value="5">5 exceedances</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <p className="text-sm text-slate-300 mb-2">Expected Results:</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
                <p>Flights with exceedances: ~{Math.round(generatorConfig.num_flights * generatorConfig.exceedance_probability)}</p>
                <p>Clean flights: ~{Math.round(generatorConfig.num_flights * (1 - generatorConfig.exceedance_probability))}</p>
                <p>Estimated exceedances: ~{Math.round(generatorConfig.num_flights * generatorConfig.exceedance_probability * (generatorConfig.max_exceedances_per_flight / 2))}</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGeneratorDialog(false)}>Cancel</Button>
            <Button onClick={generateCustomData} disabled={generating}>
              {generating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Generate Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
